import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import UsersComponent from '../../components/User Management/UsersComponent';
import UsersHero from '../../components/User Management/UsersHero';
import ImportUsersModal from '../../components/User Management/ImportUsersModal';
import UsersStatsCards from '../../components/User Management/UsersStatsCards';
import { buildUsersListUrl } from '../../components/User Management/userListQueryUrl';
import React, { useEffect, useMemo, useState } from 'react';
import UserRole from '../../../shared/AuthRoles';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { useNavigate } from 'react-router-dom';
import { useGetUserListQuery } from '../../../app/Slices/SevenDayApiSlice';

export default function UsersPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const urlSegment = window.location.href.split('/')[3];
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const theme = useTheme();

    const listUrl = useMemo(
        () => buildUsersListUrl(loggedInUser),
        [loggedInUser],
    );
    const { data: rawUsers } = useGetUserListQuery(listUrl, { skip: !listUrl });

    const isGuestShell =
        loggedInUser?.role === 'guest_admin' ||
        loggedInUser?.role === 'guest_company';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleBackButton = () => {
        if (
            loggedInUser?.role === 'owner_admin' ||
            loggedInUser?.role === 'owner_company'
        ) {
            navigate(`/adminCentre/host`);
        } else {
            navigate(`/adminCentre/guest`);
        }
    };

    const title = urlSegment === 'users' ? 'Users' : 'Clients';
    const subtitle =
        urlSegment === 'users'
            ? 'Manage people who have access to your organisation.'
            : 'Manage your clients.';

    const [showImportModal, setShowImportModal] = useState(false);

    const addLabel = useMemo(
        () =>
            urlSegment === 'clients'
                ? 'New Client'
                : loggedInUser?.role === 'guest_department_admin'
                  ? 'Add Team Member'
                  : 'Invite user',
        [urlSegment, loggedInUser?.role],
    );

    const handleInviteUser = () => {
        navigate(`/newUser`, { state: { name: 'newUser' } });
    };

    const main = (
        <Stack spacing={3} sx={{ width: '100%' }}>
            <Box>
                {isMobile ? (
                    <>
                        <Stack
                            direction='row'
                            alignItems='flex-start'
                            justifyContent='space-between'
                            spacing={1}
                        >
                            <Typography
                                variant='h1'
                                sx={{
                                    textAlign: 'left',
                                    textTransform: 'uppercase',
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                }}
                            >
                                {title}
                            </Typography>
                            <IconButton
                                color='primary'
                                onClick={handleInviteUser}
                                aria-label={addLabel}
                                sx={{
                                    mt: 0.25,
                                    flexShrink: 0,
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    },
                                }}
                            >
                                <PersonAddIcon />
                            </IconButton>
                        </Stack>
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ mt: 1.5 }}
                        >
                            {subtitle}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography
                            variant='h1'
                            sx={{
                                textAlign: 'left',
                                textTransform: 'uppercase',
                            }}
                        >
                            {title}
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent='space-between'
                            spacing={{ xs: 1.5, sm: 2 }}
                            sx={{ mt: 1.5 }}
                            useFlexGap
                        >
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ flex: '1 1 auto', minWidth: 0 }}
                            >
                                {subtitle}
                            </Typography>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                useFlexGap
                                sx={{
                                    flexShrink: 0,
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                }}
                            >
                                {loggedInUser?.role === 'guest_admin' ? (
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        startIcon={<GroupAddIcon />}
                                        onClick={() => setShowImportModal(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Import users
                                    </Button>
                                ) : null}
                                <CustomButton
                                    variant='contained'
                                    color='primary'
                                    startIcon={<AddCircleIcon />}
                                    onClick={handleInviteUser}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    {addLabel}
                                </CustomButton>
                            </Stack>
                        </Stack>
                    </>
                )}
            </Box>

            <ImportUsersModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
            />

            <UsersStatsCards users={rawUsers ?? []} />

            <Paper
                variant='outlined'
                sx={{
                    width: '100%',
                    borderRadius: 2,
                    borderColor: 'divider',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.25 } }}>
                    <UsersHero />
                </Box>
            </Paper>

            <Paper
                variant='outlined'
                sx={{
                    width: '100%',
                    borderRadius: 2,
                    borderColor: 'divider',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    <UsersComponent />
                </Box>
            </Paper>

            {isGuestShell && !isMobile && !isIpad ? (
                <Box>
                    <CustomButton
                        onClick={handleBackButton}
                        color='primary'
                        sx={{
                            marginLeft: '-8px',
                            '&:hover': {
                                cursor: 'pointer',
                                color: theme.palette.textAccent.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                    >
                        <ArrowBackIcon />{' '}
                        <Typography
                            sx={{
                                fontSize: '12px',
                                paddingLeft: '5px',
                            }}
                        >
                            Back
                        </Typography>
                    </CustomButton>
                </Box>
            ) : null}
        </Stack>
    );

    if (isGuestShell && (isMobile || isIpad)) {
        return (
            <Stack
                spacing={2}
                sx={{
                    width: '100%',
                    minHeight: isMobile || isIpad ? '65%' : undefined,
                }}
            >
                {main}
            </Stack>
        );
    }

    return <Box sx={{ width: '100%', py: { xs: 1, sm: 2 } }}>{main}</Box>;
}
